/**
 * Calendario de reuniones habituales a partir de meeting_day / meeting_time de la célula.
 */
import {
  cellMeetingDayLabel,
  cellMeetingModalityLabel,
  cellMeetingTimeLabel,
} from "@/lib/cellFormLabels";

const DAY_TO_JS = {
  domingo: 0,
  lunes: 1,
  martes: 2,
  miercoles: 3,
  jueves: 4,
  viernes: 5,
  sabado: 6,
};

function normalizeDayKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace("é", "e")
    .replace("á", "a")
    .replace("í", "i")
    .replace("ó", "o")
    .replace("ú", "u");
}

export function meetingDayToJsWeekday(meetingDay) {
  const key = normalizeDayKey(meetingDay);
  return Object.prototype.hasOwnProperty.call(DAY_TO_JS, key) ? DAY_TO_JS[key] : null;
}

function toIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseIsoDate(iso) {
  const s = String(iso || "").trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatMeetingDateDisplay(iso) {
  const d = parseIsoDate(iso);
  if (!d) return iso || "—";
  try {
    return d.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function weekdayLabelFromIso(iso) {
  const d = parseIsoDate(iso);
  if (!d) return "";
  const labels = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
  return labels[d.getDay()] || "";
}

/**
 * Últimas N fechas del día habitual (hacia atrás desde hoy, incluyendo hoy si coincide).
 * @returns {string[]} ISO YYYY-MM-DD, más reciente primero
 */
export function generateRecentHabitualDates(meetingDay, count = 8, fromDate = new Date()) {
  const target = meetingDayToJsWeekday(meetingDay);
  if (target == null || count < 1) return [];
  const out = [];
  const cursor = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  // Buscar hacia atrás hasta ~10 semanas
  for (let i = 0; i < 80 && out.length < count; i += 1) {
    if (cursor.getDay() === target) {
      out.push(toIsoDate(cursor));
    }
    cursor.setDate(cursor.getDate() - 1);
  }
  return out;
}

/**
 * Resuelve estado ingresado/pendiente por fecha habitual.
 * @param {string[]} habitualDates
 * @param {{ meeting_date: string, scheduled_meeting_date?: string|null }[]} entries
 */
export function buildMeetingHistoryRows(habitualDates, entries, meetingTime) {
  const list = Array.isArray(entries) ? entries : [];
  const byScheduled = new Map();
  const byActual = new Map();
  for (const e of list) {
    const actual = String(e?.meeting_date || "").trim();
    const scheduled = String(e?.scheduled_meeting_date || "").trim() || actual;
    if (actual) byActual.set(actual, e);
    if (scheduled) byScheduled.set(scheduled, e);
  }

  const timeLabel = cellMeetingTimeLabel(meetingTime);
  return (habitualDates || []).map((scheduledIso) => {
    const linked = byScheduled.get(scheduledIso) || byActual.get(scheduledIso) || null;
    const actualIso = linked ? String(linked.meeting_date || scheduledIso) : scheduledIso;
    const entered = Boolean(linked);
    const exceptional = entered && actualIso !== scheduledIso;
    return {
      key: scheduledIso,
      scheduledDate: scheduledIso,
      actualDate: actualIso,
      dayLabel: weekdayLabelFromIso(actualIso) || cellMeetingDayLabel(scheduledIso) || weekdayLabelFromIso(scheduledIso),
      timeLabel,
      status: entered ? "ingresado" : "pendiente",
      exceptional,
      suspended: Boolean(linked?.meeting_suspended),
    };
  });
}

export function complianceFromRows(rows) {
  const total = Array.isArray(rows) ? rows.length : 0;
  const entered = (rows || []).filter((r) => r.status === "ingresado").length;
  const pending = total - entered;
  const pct = total ? Math.round((entered / total) * 1000) / 10 : 0;
  return { total, entered, pending, pct };
}

export function cellScheduleSummaryLabels(cell) {
  return {
    dayLabel: cellMeetingDayLabel(cell?.meeting_day) || "Sin día asignado",
    timeLabel: cellMeetingTimeLabel(cell?.meeting_time),
    modalityLabel: cellMeetingModalityLabel(cell?.meeting_modality, { short: true }),
  };
}
